import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaShoppingBag,
  FaUser,
  FaBars,
  FaTimes,
  FaHeart,
  FaSignOutAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const navigate = useNavigate();

  // 🧍 Load user
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("accessToken");

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else if (token) {
      fetch("/api/profile/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.username) {
            localStorage.setItem("user", JSON.stringify(data));
            setUser(data);
          } else {
            setUser(null);
          }
        })
        .catch(() => setUser(null));
    }
  }, []);

  // 🛒 Update cart count
  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartCount(cart.reduce((sum, item) => sum + (item.quantity || 1), 0));
    };
    updateCartCount();
    window.addEventListener("storage", updateCartCount);
    return () => window.removeEventListener("storage", updateCartCount);
  }, []);

  // 🔍 Search function
  useEffect(() => {
    if (query.trim() === "") {
      setResults([]);
      return;
    }

    const delay = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search/?q=${query}`);
        const data = await res.json();
        setResults(data);
        setShowDropdown(true);
      } catch (err) {
        console.error("Search error:", err);
      }
    }, 400);

    return () => clearTimeout(delay);
  }, [query]);

  // ❌ Close dropdowns outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        (dropdownRef.current && !dropdownRef.current.contains(e.target)) &&
        (profileRef.current && !profileRef.current.contains(e.target))
      ) {
        setShowDropdown(false);
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🚪 Logout function
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("cart");

    setUser(null);
    setShowProfileMenu(false);
    window.dispatchEvent(new Event("storage"));
    navigate("/login");
  };

  return (
    <>
      {/* 🧾 Announcement Bar */}
      <div className="bg-gray-100 text-gray-700 py-2 text-center text-sm font-medium fixed top-0 w-full z-50 border-b border-gray-200">
        ✨ Free Shipping on Orders Above ₹999 | New Styles Added Daily!
      </div>

      {/* 🧭 Navbar */}
      <nav className="bg-white/90 backdrop-blur-md fixed top-[32px] w-full z-50 shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-5 py-3 flex justify-between items-center">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl font-semibold tracking-wider text-gray-800 hover:text-gray-600 transition-all duration-300"
          >
            33Th<span className="font-light text-gray-500">reads</span>
          </Link>

          {/* Menu */}
          <ul className="hidden md:flex items-center gap-8 text-gray-700 text-sm font-medium">
            {["Home", "Collections", "About", "Contact"].map((item) => (
              <motion.li key={item} whileHover={{ y: -2 }}>
                <Link
                  to={
                    item === "Home"
                      ? "/"
                      : `/${item.toLowerCase().replace(" ", "-")}`
                  }
                  className="hover:text-gray-900 transition"
                >
                  {item}
                </Link>
              </motion.li>
            ))}
          </ul>

          {/* Right Side */}
          <div className="flex items-center gap-4" ref={dropdownRef}>
            {/* Search */}
            <div className="relative">
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-1.5 w-40 sm:w-56 md:w-64 shadow-inner">
                <FaSearch className="text-gray-500 mr-2 text-sm" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  className="bg-transparent outline-none text-sm w-full text-gray-700"
                />
              </div>

              {showDropdown && results.length > 0 && (
                <div className="absolute top-11 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {results.map((item) => (
                    <Link
                      key={item.id}
                      to={`/collections?product=${item.id}`}
                      onClick={() => {
                        setShowDropdown(false);
                        setQuery("");
                      }}
                      className="block px-4 py-2 hover:bg-gray-100 transition text-sm text-gray-800 flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          ₹{item.offer_price || item.price}
                        </p>
                      </div>
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-10 h-10 object-cover rounded-md"
                        />
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Cart & Profile */}
            <div className="hidden md:flex items-center gap-4 text-gray-700">
              <FaHeart className="text-lg cursor-pointer hover:text-gray-900 transition" />

              <Link to="/cart" className="relative hover:text-gray-900 transition">
                <FaShoppingBag className="text-lg" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <FaUser
                  className="text-lg cursor-pointer hover:text-gray-900 transition"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                />
                {showProfileMenu && (
                  <div className="absolute right-0 mt-3 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-[9999]">
{user ? (
  <>
    <p className="px-4 py-2 text-sm text-gray-700 border-b">
      Hi, <span className="font-medium">{user.username}</span>
    </p>

    <Link
      to="/profile"
      className="block px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2"
      onClick={() => setShowProfileMenu(false)}
    >
      <FaUser className="w-4 h-4" />
      My Profile
    </Link>

    <Link
      to="/orders"
      className="block px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2"
      onClick={() => setShowProfileMenu(false)}
    >
      <FaShoppingBag className="w-4 h-4" />
      My Orders
    </Link>

    <button
      onClick={handleLogout}
      className="flex items-center gap-2 px-4 py-2 text-sm w-full text-left hover:bg-gray-100 text-red-600"
    >
      <FaSignOutAlt className="w-4 h-4" />
      Logout
    </button>
  </>
) : (
  <>
    <Link
      to="/login"
      className="block px-4 py-2 text-sm hover:bg-gray-100 text-gray-700"
      onClick={() => setShowProfileMenu(false)}
    >
      Login
    </Link>
    <Link
      to="/register"
      className="block px-4 py-2 text-sm hover:bg-gray-100 text-gray-700"
      onClick={() => setShowProfileMenu(false)}
    >
      Sign Up
    </Link>
  </>
)}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-gray-800 text-2xl ml-2"
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

{/* Mobile Menu */}
{isOpen && (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    className="md:hidden bg-white/95 backdrop-blur-md fixed top-[64px] left-0 w-full h-screen overflow-y-auto flex flex-col pt-10 pb-20 px-6 text-gray-800 z-[999]"
  >
    {/* Navigation Links */}
    <div className="flex flex-col items-center space-y-8 mb-12">
      {["Home", "Collections", "About", "Contact"].map((item) => (
        <Link
          key={item}
          to={
            item === "Home"
              ? "/"
              : `/${item.toLowerCase().replace(" ", "-")}`
          }
          onClick={() => setIsOpen(false)}
          className="text-xl font-medium hover:text-black transition"
        >
          {item}
        </Link>
      ))}
    </div>

    {/* Divider */}
    <div className="w-16 h-px bg-gray-300 mx-auto mb-10"></div>

    {/* User Actions */}
    <div className="flex flex-col items-center space-y-8">
      {/* Cart */}
      <Link
        to="/cart"
        onClick={() => setIsOpen(false)}
        className="flex items-center gap-3 text-xl font-medium hover:text-black transition"
      >
        <div className="relative">
          <FaShoppingBag className="text-2xl" />
          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {cartCount}
            </span>
          )}
        </div>
        <span>Cart</span>
      </Link>

      {/* Wishlist */}
      <Link
        to="/wishlist" // ← change route if you have different path
        onClick={() => setIsOpen(false)}
        className="flex items-center gap-3 text-xl font-medium hover:text-black transition"
      >
        <FaHeart className="text-2xl" />
        <span>Wishlist</span>
      </Link>

      {/* Profile / Auth Section */}
      {user ? (
        <div className="flex flex-col items-center space-y-6 w-full">
          <div className="text-center">
            <p className="text-lg font-medium">Hi, {user.username}</p>
          </div>

          <Link
            to="/profile"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 text-xl font-medium hover:text-black w-full justify-center"
          >
            <FaUser className="text-2xl" />
            <span>My Profile</span>
          </Link>

          <Link
            to="/orders"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 text-xl font-medium hover:text-black w-full justify-center"
          >
            <FaShoppingBag className="text-2xl" />
            <span>My Orders</span>
          </Link>

          <button
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            className="flex items-center gap-3 text-xl font-medium text-red-600 hover:text-red-700 w-full justify-center"
          >
            <FaSignOutAlt className="text-2xl" />
            <span>Logout</span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center space-y-6 w-full">
          <Link
            to="/login"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 text-xl font-medium hover:text-black w-full justify-center"
          >
            <FaUser className="text-2xl" />
            <span>Login</span>
          </Link>

          <Link
            to="/register"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 text-xl font-medium hover:text-black w-full justify-center"
          >
            <FaUser className="text-2xl" />
            <span>Sign Up</span>
          </Link>
        </div>
      )}
    </div>
  </motion.div>
)}
      </nav>
    </>
  );
}
